import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const YearPreparation = () => {
  const [academicYears, setAcademicYears] = useState([]);
  const [sourceYear, setSourceYear] = useState('');
  const [targetYear, setTargetYear] = useState('');
  const [overview, setOverview] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [activeTab, setActiveTab] = useState('classes');
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classAssignments, setClassAssignments] = useState({});
  const [principalId, setPrincipalId] = useState('');

  useEffect(() => {
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    if (sourceYear && targetYear) {
      fetchOverview();
    }
  }, [sourceYear, targetYear]);

  const fetchAcademicYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/admin/academic-years', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const years = Array.isArray(res.data) ? res.data : [];
      setAcademicYears(years);

      const current = years.find((y) => y.is_current && !y.is_closed);
      if (current) {
        setSourceYear(current.year);
        const next = years
          .filter((y) => y.year > current.year && !y.is_closed)
          .sort((a, b) => a.year.localeCompare(b.year))[0];
        if (next) setTargetYear(next.year);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get(`/admin/year-preparation/overview?source_year=${sourceYear}&target_year=${targetYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async (year) => {
    try {
      const token = localStorage.getItem('token');
      const allClasses = await api.get('/classes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const filtered = (Array.isArray(allClasses.data) ? allClasses.data : [])
        .filter((c) => c.academic_year === year);
      setClasses(filtered);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchClassDetails = async (classId, year) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get(`/admin/year-preparation/class-details?class_id=${classId}&academic_year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedClass(res.data.class);
      setSubjects(res.data.subjects || []);
      setTeachers(res.data.teachers || []);
      setPrincipalId(res.data.principal_id || '');

      // Construire l'objet assignments
      const assignObj = {};
      (res.data.subjects || []).forEach((s) => {
        assignObj[s.subject_id] = s.teacher_id || '';
      });
      setClassAssignments(assignObj);
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignments = async () => {
    if (!selectedClass) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const assignmentsPayload = subjects.map((s) => ({
        subject_id: s.subject_id,
        teacher_id: classAssignments[s.subject_id] || null,
        coefficient: s.coefficient,
      }));

      const res = await api.post('/admin/year-preparation/save-assignments', {
        class_id: selectedClass.id,
        academic_year: targetYear,
        assignments: assignmentsPayload,
        principal_id: principalId || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ ' + res.data.message);
      fetchClassDetails(selectedClass.id, targetYear);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCloneClasses = async () => {
    if (!window.confirm(`Cloner les classes de ${sourceYear} vers ${targetYear} ?`)) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.post('/admin/year-preparation/clone-classes', {
        source_year: sourceYear,
        target_year: targetYear,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ ' + res.data.message);
      fetchClasses(targetYear);
      fetchOverview();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!window.confirm(`⚠️ ATTENTION :\n\nVous allez :\n1. Clôturer ${sourceYear}\n2. Activer ${targetYear}\n\nContinuer ?`)) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.post('/admin/year-preparation/activate', {
        source_year: sourceYear,
        target_year: targetYear,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ ' + res.data.message);
      fetchAcademicYears();
      fetchOverview();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'teachers' && targetYear) {
      fetchClasses(targetYear);
    }
    if (activeTab === 'classes' && targetYear) {
      fetchClasses(targetYear);
    }
  }, [activeTab, targetYear]);

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#0A0A0A]">🎓 Préparation de la nouvelle année</h1>
        <p className="text-gray-500 mt-1">Assistant complet pour préparer la rentrée scolaire</p>
      </div>

      {/* SÉLECTIONS ANNÉES */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">📤 Année actuelle</label>
            <select
              value={sourceYear}
              onChange={(e) => setSourceYear(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">-- Choisir --</option>
              {academicYears.filter((y) => !y.is_closed).map((y) => (
                <option key={y.id} value={y.year}>
                  {y.year} {y.is_current ? '✅ (en cours)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">📥 Nouvelle année</label>
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">-- Choisir --</option>
              {academicYears
                .filter((y) => !y.is_closed && y.year > sourceYear)
                .sort((a, b) => a.year.localeCompare(b.year))
                .map((y) => (
                  <option key={y.id} value={y.year}>{y.year}</option>
                ))}
            </select>
          </div>
          <div className="flex items-end">
            {sourceYear && targetYear && (
              <div className="w-full bg-green-50 rounded-lg p-3 text-sm text-green-800">
                Passage : <strong>{sourceYear}</strong> → <strong>{targetYear}</strong>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ONGLETS */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100">
          {[
            { id: 'students', label: '1. Élèves', icon: '👥' },
            { id: 'classes', label: '2. Classes', icon: '🏫' },
            { id: 'teachers', label: '3. Profs', icon: '👨‍🏫' },
            { id: 'validate', label: '4. Valider', icon: '✅' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-4 font-medium transition ${
                activeTab === tab.id
                  ? 'bg-[#F39C12] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* ONGLET 1 : ÉLÈVES */}
          {activeTab === 'students' && (
            <div>
              <h2 className="text-xl font-bold mb-4">👥 Passation des élèves</h2>
              <div className="bg-blue-50 rounded-xl p-6 text-center">
                <p className="text-4xl mb-3">👥</p>
                <p className="font-bold text-lg mb-2">Passation des élèves</p>
                <p className="text-sm text-gray-600 mb-4">
                  La page de passation des élèves est séparée pour plus de clarté.
                </p>
                <a
                  href="/admin/enrollments"
                  className="inline-block bg-[#F39C12] text-white px-6 py-3 rounded-lg hover:bg-[#E67E22] font-medium"
                >
                  📋 Aller à la passation des élèves →
                </a>
              </div>
            </div>
          )}

          {/* ONGLET 2 : CLASSES */}
          {activeTab === 'classes' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">🏫 Classes pour {targetYear}</h2>
                <button
                  onClick={handleCloneClasses}
                  disabled={loading || !sourceYear || !targetYear}
                  className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 font-medium disabled:opacity-50"
                >
                  📋 Cloner les classes de {sourceYear} → {targetYear}
                </button>
              </div>

              {classes.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                  <p className="text-3xl mb-2">⚠️</p>
                  <p className="font-bold mb-2">Aucune classe pour {targetYear}</p>
                  <p className="text-sm text-gray-600 mb-4">
                    Cliquez sur "Cloner les classes" pour les créer automatiquement.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {classes.map((c) => (
                    <div key={c.id} className="bg-white rounded-xl border border-gray-100 p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-lg">{c.name}</p>
                          <p className="text-xs text-gray-500">{c.level} — {c.academic_year}</p>
                        </div>
                        {c.class_teacher_id && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            👑 Prof principal
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        {c.students_count || 0} / {c.max_students} élèves
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ONGLET 3 : PROFS */}
          {activeTab === 'teachers' && (
            <div>
              <h2 className="text-xl font-bold mb-4">👨‍🏫 Affectation des professeurs</h2>

              {classes.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                  <p className="text-3xl mb-2">⚠️</p>
                  <p className="font-bold mb-2">Aucune classe pour {targetYear}</p>
                  <p className="text-sm text-gray-600">
                    Allez d'abord dans l'onglet "2. Classes" pour créer les classes.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Liste des classes */}
                  <div className="lg:col-span-1 bg-gray-50 rounded-xl p-4">
                    <h3 className="font-bold mb-3 text-sm text-gray-600">🏫 CLASSES</h3>
                    <div className="space-y-2">
                      {classes.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => fetchClassDetails(c.id, targetYear)}
                          className={`w-full text-left px-3 py-2 rounded-lg transition ${
                            selectedClass?.id === c.id
                              ? 'bg-[#F39C12] text-white'
                              : 'bg-white hover:bg-orange-50'
                          }`}
                        >
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs opacity-75">{c.level}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Détails de la classe */}
                  <div className="lg:col-span-2">
                    {!selectedClass ? (
                      <div className="bg-blue-50 rounded-xl p-8 text-center">
                        <p className="text-4xl mb-3">👈</p>
                        <p className="font-bold">Sélectionnez une classe</p>
                        <p className="text-sm text-gray-600">Pour affecter les professeurs</p>
                      </div>
                    ) : (
                      <div>
                        <div className="bg-orange-50 rounded-xl p-4 mb-4">
                          <h3 className="font-bold text-lg">{selectedClass.name}</h3>
                          <p className="text-sm text-gray-600">
                            {selectedClass.level} — {selectedClass.academic_year}
                          </p>
                        </div>

                        {/* Professeur principal */}
                        <div className="bg-yellow-50 rounded-xl p-4 mb-4">
                          <label className="block text-sm font-bold text-yellow-800 mb-2">
                            👑 Professeur principal
                          </label>
                          <select
                            value={principalId}
                            onChange={(e) => setPrincipalId(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          >
                            <option value="">-- Aucun --</option>
                            {teachers.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} {t.role === 'principal_teacher' ? '(Prof. Principal)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Matières */}
                        <div className="space-y-3 mb-4">
                          <h4 className="font-bold text-sm text-gray-600">📚 MATIÈRES ET PROFESSEURS</h4>
                          {subjects.map((s) => (
                            <div key={s.subject_id} className="flex items-center gap-3 bg-white rounded-lg p-3 border">
                              <div className="flex-1">
                                <p className="font-medium text-sm">{s.subject_name}</p>
                                <p className="text-xs text-gray-500">Coef {s.coefficient}</p>
                              </div>
                              <select
                                value={classAssignments[s.subject_id] || ''}
                                onChange={(e) => setClassAssignments((prev) => ({
                                  ...prev,
                                  [s.subject_id]: e.target.value,
                                }))}
                                className="px-3 py-2 border rounded-lg text-sm min-w-[200px]"
                              >
                                <option value="">-- Aucun prof --</option>
                                {teachers.map((t) => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={handleSaveAssignments}
                          disabled={loading}
                          className="w-full bg-[#F39C12] text-white py-3 rounded-lg hover:bg-[#E67E22] font-bold disabled:opacity-50"
                        >
                          {loading ? '⏳ Enregistrement...' : '💾 Enregistrer les affectations'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ONGLET 4 : VALIDER */}
          {activeTab === 'validate' && (
            <div>
              <h2 className="text-xl font-bold mb-4">✅ Récapitulatif et validation</h2>

              {overview && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <CheckCard
                      label="Classes créées pour {targetYear}"
                      value={overview.stats.target_classes}
                      ok={overview.checks.classes_created}
                    />
                    <CheckCard
                      label="Élèves passés"
                      value={overview.stats.students_promoted}
                      ok={overview.checks.students_promoted}
                    />
                    <CheckCard
                      label="Profs affectés"
                      value={overview.stats.assignments_target}
                      ok={overview.checks.teachers_assigned}
                    />
                    <CheckCard
                      label="Profs principaux désignés"
                      value={overview.stats.principals_target}
                      ok={overview.checks.principals_set}
                    />
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                    <p className="font-bold text-red-800 mb-2">⚠️ Attention</p>
                    <p className="text-sm text-red-700">
                      En cliquant sur "ACTIVER LA NOUVELLE ANNÉE" :
                    </p>
                    <ul className="list-disc ml-6 mt-2 text-sm text-red-700">
                      <li>L'année <strong>{sourceYear}</strong> sera clôturée</li>
                      <li>L'année <strong>{targetYear}</strong> deviendra l'année en cours</li>
                      <li>Tous les profs et élèves verront leur nouvelle classe</li>
                      <li>L'historique de {sourceYear} sera conservé</li>
                    </ul>
                  </div>

                  <button
                    onClick={handleActivate}
                    disabled={loading}
                    className="w-full bg-green-500 text-white py-4 rounded-xl hover:bg-green-600 font-bold text-lg disabled:opacity-50"
                  >
                    {loading ? '⏳ Traitement...' : `🎯 ACTIVER L'ANNÉE ${targetYear}`}
                  </button>
                </>
              )}

              {!overview && (
                <div className="text-center py-12 text-gray-500">
                  Sélectionnez les années en haut pour voir le récapitulatif
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function CheckCard({ label, value, ok }) {
  return (
    <div className={`rounded-xl p-4 border-2 ${ok ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <div className="flex items-center justify-between">
        <p className={`text-2xl font-bold ${ok ? 'text-green-600' : 'text-gray-600'}`}>{value}</p>
        <span className="text-2xl">{ok ? '✅' : '⏳'}</span>
      </div>
    </div>
  );
}

export default YearPreparation;